import type { RouterContextType } from "@/api/permissions/permissions.api";
import VerticalStepperLayout from "@/components/features/VerticalStepperLayout/VerticalStepperLayout";
import type { Step } from "@/components/ui/VerticalStepper/VerticalStepper";
import { useClientOnboardingStore } from "@/store/clientOnboardingStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo } from "react";

export const Route = createFileRoute("/clients/add-new-client/_addNewClientLayout")({
  component: AddNewClientLayout,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

type StepConfigSubStep = { id: string; stepKey: string; label: string };
type StepConfigItem = {
  id: string;
  stepKey: string;
  label: string;
  subSteps: StepConfigSubStep[];
};

// Client creation captures only Basic Information, Brands, and Locations.
// Assets, Contacts, Coverage, Rates, Payment & Invoicing, and Check-in/out are
// filled in after creation from the client's own detail tabs.
export const stepConfig: StepConfigItem[] = [
  { id: "1", stepKey: "basic-info", label: "Basic Information", subSteps: [] },
  { id: "2", stepKey: "brands", label: "Brands", subSteps: [] },
  { id: "3", stepKey: "locations", label: "Locations", subSteps: [] },
];

function AddNewClientLayout() {
  const navigate = useNavigate();
  // Inside component:
  const location = useLocation();
  // Extract stepId from pathname since it's in a child route
  const stepId = location.pathname.split("/clients/add-new-client/")[1]?.split("/")[0] || "";
  const { currentStep, currentSubStep, setCurrentStep, setCurrentSubStep, setNavigationHandlers } =
    useClientOnboardingStore();

  const isValidStepRoute = useMemo(() => {
    const stepKeys = stepConfig.map((step) => step.stepKey);
    const isStepRoute = stepKeys.some((stepKey) => {
      return (
        location.pathname === `/clients/add-new-client/${stepKey}` ||
        location.pathname.includes(`/clients/add-new-client/${stepKey}`)
      );
    });
    return isStepRoute;
  }, [location.pathname]);

  useEffect(() => {
    //only check for valid routes when we are in add new client flow
    //allow if we are no longer in that flow
    const isAddClientRoute = location.pathname.includes("/clients/add-new-client");

    // Don't run any logic if we're not in the add-new-client route anymore
    // This prevents redirects when navigating away
    if (!isAddClientRoute) {
      return;
    }

    if (!isValidStepRoute) {
      navigate({
        to: "/clients",
        replace: true,
      });
      return;
    }

    // Find the step config for the current stepId
    const currentStepFromUrl = stepConfig.find((step) => step.stepKey === stepId);

    if (currentStepFromUrl) {
      setCurrentStep(Number(currentStepFromUrl.id));

      // Reset substep to 1 when navigating via browser (not via next/previous buttons)
      // This ensures substeps are also synced correctly
      setCurrentSubStep(1);
    }
  }, [isValidStepRoute, navigate, stepId, setCurrentStep, setCurrentSubStep, location.pathname]);

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex + 1 < currentStep) return "completed";
    if (stepIndex + 1 === currentStep) return "current";
    return "pending";
  };

  const getSubStepStatus = (stepIndex: number, subStepIndex: number) => {
    if (stepIndex + 1 < currentStep) return "completed";
    if (stepIndex + 1 === currentStep) {
      if (subStepIndex + 1 < currentSubStep) return "completed";
      if (subStepIndex + 1 === currentSubStep) return "current";
    }
    return "pending";
  };

  const steps: Step[] = stepConfig.map((step, stepIndex) => ({
    ...step,
    status: getStepStatus(stepIndex),
    subSteps:
      step.subSteps.length > 0
        ? step.subSteps.map((sub, subIndex) => ({
            ...sub,
            status: getSubStepStatus(stepIndex, subIndex),
          }))
        : undefined,
  }));

  const currentStepConfig = stepConfig[currentStep - 1];
  const hasSubSteps = currentStepConfig.subSteps.length > 0;
  const totalSubSteps = hasSubSteps ? currentStepConfig.subSteps.length : 0;

  const handleNext = useCallback(() => {
    if (hasSubSteps && currentSubStep < totalSubSteps) {
      // Go to next substep
      setCurrentSubStep(currentSubStep + 1);
    } else {
      // Navigate to next step
      const nextStepIndex = currentStep;
      setCurrentStep(currentStep + 1);
      setCurrentSubStep(1); // Reset substep when moving to new step
      if (nextStepIndex < stepConfig.length) {
        const nextStepKey = stepConfig[nextStepIndex].stepKey;
        navigate({
          to: `/clients/add-new-client/${nextStepKey}`,
        });
      }
    }
  }, [
    hasSubSteps,
    currentSubStep,
    totalSubSteps,
    currentStep,
    setCurrentStep,
    setCurrentSubStep,
    navigate,
  ]);

  const isFirstStep = currentStep === 1 && currentSubStep === 1;
  const isLastStep = currentStep === stepConfig.length;

  const handlePrevious = useCallback(() => {
    if (hasSubSteps && currentSubStep > 1) {
      // Go to previous substep
      setCurrentSubStep(currentSubStep - 1);
    } else if (currentStep > 1) {
      // Go to previous main step
      const prevStepConfig = stepConfig[currentStep - 2];
      const prevHasSubSteps = prevStepConfig.subSteps.length > 0;
      const previousStepIndex = currentStep - 2;
      setCurrentStep(currentStep - 1);
      const prevStepKey = stepConfig[previousStepIndex].stepKey;

      navigate({
        to: `/clients/add-new-client/${prevStepKey}`,
      });

      // If previous step has substeps, go to its last substep
      setCurrentSubStep(prevHasSubSteps ? prevStepConfig.subSteps.length : 1);
    }
  }, [
    hasSubSteps,
    currentSubStep,
    currentStep,
    setCurrentStep,
    setCurrentSubStep,
    navigate,
    isLastStep,
  ]);

  // Set navigation handlers in store so child components can access them
  useEffect(() => {
    setNavigationHandlers({
      onNext: handleNext,
      onPrevious: handlePrevious,
      isFirstStep,
      isLastStep,
    });
  }, [handleNext, handlePrevious, isFirstStep, isLastStep, setNavigationHandlers]);

  return (
    <div>
      <VerticalStepperLayout headerLabel="Add New Client" steps={steps} defaultBackUrl="/clients">
        {/* {renderStepContent()} */}
        <Outlet />
      </VerticalStepperLayout>
    </div>
  );
}
